<template>
  <div class="container d-flex flex-column min-vh-100 mb-0">
    <div class="row justify-content-center flex-grow-1 align-items-center">
      <div class="col-10 col-md-7 col-lg-5 col-xl-4 mx-auto">
        <div id="card-black" class="card mt-0">
          <div id="card-black" class="card-header p-0 overflow-hidden">
            <div class="header-container d-flex justify-content-center align-items-center">
              <img :src="brandLogo" :alt="brandName + ' Logo'" class="logo-image" :class="brandLogoClass" />
            </div>
          </div>
          <div class="card-body">
            <div v-if="linkStatus === 'checking'" class="text-center text-white py-4">
              {{ $t('registration.checkingLink') }}
            </div>
            <div v-else-if="linkStatus !== 'valid'" class="text-center text-white py-4">
              <p class="mb-2">
                {{ linkStatus === 'completed' ? $t('registration.linkCompleted')
                  : linkStatus === 'expired' ? $t('registration.linkExpired')
                  : $t('registration.linkInvalid') }}
              </p>
              <p v-if="linkStatus !== 'completed'" class="mb-0">{{ $t('registration.contactAdmin') }}</p>
            </div>
            <Form v-else role="form" class="text-start mt-3" :validation-schema="schema" @submit="submit">
              <div class="mb-3">
                <material-input-field id="username" v-model="username" type="text"
                  :label="$t('users.username') + ' *'" name="username" variant="static"
                  @input="username = username.replace(/\s/g, '')"
                  @blur="checkUsernameAvailability" />
                <small v-if="usernameError" class="text-danger">{{ usernameError }}</small>
              </div>
              <div class="mb-3">
                <material-input-field id="firstName" v-model="firstName" type="text"
                  :label="$t('users.firstName') + ' *'" name="firstName" variant="static" />
              </div>
              <div class="mb-3">
                <material-input-field id="lastName" v-model="lastName" type="text"
                  :label="$t('users.lastName') + ' *'" name="lastName" variant="static" />
              </div>
              <div class="mb-3">
                <material-input-field id="identificationNumber" v-model="identificationNumber" type="text"
                  :label="$t('users.identificationNumber') + ' *'" name="identificationNumber" variant="static" />
              </div>
              <div class="mb-3">
                <material-input-field id="phoneNumber" v-model="phoneNumber" type="text"
                  :label="$t('users.phoneNumber') + ' *'" name="phoneNumber" variant="static" />
              </div>
              <div class="mb-3">
                <material-input-field id="newPassword" v-model="password" type="password"
                  :label="$t('users.password') + ' *'" name="newPassword" variant="static" />
              </div>
              <div class="mb-3">
                <material-input-field id="confirmPassword" v-model="confirmation" type="password"
                  :label="$t('users.confirmPassword') + ' *'" name="confirmPassword" variant="static" />
              </div>
              <div class="mb-3">
                <label class="col-form-label text-white">{{ $t('users.language') }} *</label>
                <el-select v-model="language" :placeholder="$t('users.selectLanguage')" :class="selectClass" size="large" class="w-100">
                  <el-option
                    v-for="(info, code) in languageMap"
                    :key="code"
                    :label="`${info.flag} ${info.name}`"
                    :value="code"
                  />
                </el-select>
              </div>
              <div v-if="termsBody" class="mb-3">
                <label class="col-form-label text-white">{{ $t('terms.title') }} *</label>
                <div class="terms-preview">
                  <pre class="terms-pre">{{ termsBody }}</pre>
                </div>
                <div class="form-check mt-2">
                  <input id="acceptTerms" v-model="acceptTerms" type="checkbox" class="form-check-input" />
                  <label class="form-check-label text-white" for="acceptTerms">
                    {{ $t('terms.acceptCheckbox') }}
                  </label>
                </div>
              </div>
              <div class="text-center">
                <material-button class="my-4 mb-2" variant="gradient" :color="brandButtonColor" full-width type="submit"
                  :disabled="submitting || !usernameValid || !acceptTerms || !termsId">
                  {{ $t('registration.completeSubmit') }}
                </material-button>
              </div>
            </Form>
          </div>
          <div class="card-footer text-center pt-0 px-sm-4 px-1 mt-4">
            <p class="mb-2 mx-auto">
              <router-link :to="{ name: 'Login' }" class="text-white font-weight-bold">
                {{ $t('sidebar.login') }}
              </router-link>
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
  <app-footer />
</template>

<script>
import branding from "../branding";
import api from "../services/api";
import { getApiErrorMessage } from "../utils/apiError";
import AppFooter from "@/examples/Footer.vue";
import MaterialInputField from "@/components/MaterialInputField.vue";
import MaterialButton from "@/components/MaterialButton.vue";
import { ElSelect, ElOption } from 'element-plus';
const body = document.getElementsByTagName("body")[0];
import { mapMutations } from "vuex";
import { Form } from "vee-validate";
import * as Yup from 'yup';

export default {
  name: "ConfirmRegistration",
  components: {
    AppFooter,
    MaterialInputField,
    MaterialButton,
    ElSelect,
    ElOption,
    Form
  },
  data() {
    return {
      brandLogo: branding.loginLogo,
      brandName: branding.name,
      brandLogoClass: branding.loginLogoClass,
      brandButtonColor: branding.buttonColor,
      username: '',
      firstName: '',
      lastName: '',
      identificationNumber: '',
      phoneNumber: '',
      password: '',
      confirmation: '',
      language: '',
      registrationCode: '',
      linkStatus: 'checking',
      submitting: false,
      usernameError: '',
      termsId: null,
      termsBody: '',
      acceptTerms: false,
      languageMap: {
        'en-US': { name: 'English', flag: '\u{1F1FA}\u{1F1F8}' },
        'is-IS': { name: 'Íslenska', flag: '\u{1F1EE}\u{1F1F8}' },
        'es-ES': { name: 'Español', flag: '\u{1F1EA}\u{1F1F8}' },
        'ro-RO': { name: 'Română', flag: '\u{1F1F7}\u{1F1F4}' },
        'lt-LT': { name: 'Lietuvių', flag: '\u{1F1F1}\u{1F1F9}' },
        'pl-PL': { name: 'Polski', flag: '\u{1F1F5}\u{1F1F1}' },
        'sl-SI': { name: 'Slovenščina', flag: '\u{1F1F8}\u{1F1EE}' },
        'sk-SK': { name: 'Slovenčina', flag: '\u{1F1F8}\u{1F1F0}' },
      },
      schema: Yup.object().shape({
        username: Yup.string().required("Username is a required input")
          .test('no-spaces', 'Username must be one word (no spaces)', value => !/\s/.test(value)),
        firstName: Yup.string().required("First name is a required input"),
        lastName: Yup.string().required("Last name is a required input"),
        identificationNumber: Yup.string().required("Identification number is a required input"),
        phoneNumber: Yup.string().required("Phone number is a required input"),
        newPassword: Yup.string()
          .required("Password is a required input")
          .min(8, "Password must have at least 8 characters")
          .matches(/[a-z]/, "Must contain at least one lowercase letter")
          .matches(/[A-Z]/, "Must contain at least one uppercase letter")
          .matches(/[0-9]/, "Must contain at least one number")
          .matches(/[^A-Za-z0-9]/, "Must contain at least one special character"),
        confirmPassword: Yup.string()
          .required("Confirm password is a required input")
          .oneOf([Yup.ref('newPassword')], 'Passwords do not match'),
      }),
    };
  },
  computed: {
    usernameValid() {
      return this.username && !/\s/.test(this.username);
    },
    selectClass() {
      let d = localStorage.getItem('darkMode');
      return (d === null || d === 'false') ? 'select-standard' : 'select-primary';
    }
  },
  created() {
    this.registrationCode = this.$route.query.code;
    this.validateLink();
    this.loadTerms();
  },
  beforeMount() {
    this.toggleEveryDisplay();
    this.toggleHideConfig();
    body.classList.remove("bg-gray-100");
  },
  beforeUnmount() {
    this.toggleEveryDisplay();
    this.toggleHideConfig();
    body.classList.add("bg-gray-100");
  },
  methods: {
    ...mapMutations(["toggleEveryDisplay", "toggleHideConfig"]),
    async validateLink() {
      if (!this.registrationCode) {
        this.linkStatus = 'invalid';
        return;
      }
      try {
        const response = await api.get('/identity/ValidateRegistrationCode', {
          params: { code: this.registrationCode }
        });
        // reason: "valid" | "completed" | "expired" | "invalid"
        this.linkStatus = response.data?.reason || 'invalid';
      } catch (error) {
        console.error('Registration link validation failed:', error); // eslint-disable-line no-console
        this.linkStatus = 'invalid';
      }
    },
    async checkUsernameAvailability() {
      if (!this.username) {
        this.usernameError = '';
        return;
      }
      try {
        const response = await api.post('/identity/CheckUsername', { username: this.username });
        if (response.data.value) {
          this.usernameError = this.$t('errors.usernameTaken');
        } else {
          this.usernameError = '';
        }
      } catch (error) {
        this.usernameError = this.$t('errors.usernameCheckFailed');
        console.error('Username check error:', error); // eslint-disable-line no-console
      }
    },
    async loadTerms() {
      try {
        const response = await api.get('/TermsAndConditions/latest');
        this.termsId = response.data.id;
        this.termsBody = response.data.body;
      } catch (error) {
        console.error('Failed to load terms:', error); // eslint-disable-line no-console
      }
    },
    async submit() {
      if (this.submitting) {
        return;
      }
      if (!this.acceptTerms || !this.termsId) {
        this.$toast.error(this.$t('terms.mustAccept'), { timeout: 4000 });
        return;
      }
      this.submitting = true;
      try {
        await api.post('/identity/CompleteRegistration', {
          registrationCode: this.registrationCode,
          userName: this.username,
          firstName: this.firstName,
          lastName: this.lastName,
          identificationNumber: this.identificationNumber,
          phoneNumber: this.phoneNumber,
          password: this.password,
          language: this.language || 'en-US',
          acceptedTermsAndConditionsId: this.termsId,
        });
        this.$toast.success(this.$t('registration.completeSuccess'), { timeout: 2000 });
        this.$router.push({ name: 'Login' });
      } catch (error) {
        console.error('Registration completion failed:', error); // eslint-disable-line no-console
        const identifier = error?.response?.data?.[0]?.identifier
          || error?.response?.data?.errors?.[0]?.identifier;
        if (identifier === 'RegistrationLinkExpired') {
          this.linkStatus = 'expired';
          return;
        }
        this.$toast.error(getApiErrorMessage(this.$t, error), { timeout: 0 });
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>
<style scoped>
#card-black {
  background: #27293d;
}

.container {
  padding-top: 0;
  padding-bottom: 0;
}

.header-container {
  min-height: 200px;
  background-color: transparent;
  padding: 24px 20px;
  overflow: hidden;
}

.logo-image {
  width: 80%;
  max-width: 360px;
  height: auto;
  object-fit: contain;
  display: block;
}

.logo-image.logo-tolspor {
  transform: scale(2);
}

.logo-image.logo-golfspor {
  width: 100%;
  max-width: 100%;
  transform: scale(1.8);
}

@media (max-width: 767.98px) {
  .header-container {
    min-height: 140px;
    padding: 16px;
  }
}

:deep(.input-group-static .form-control) {
  padding-top: 12px;
  padding-bottom: 10px;
}

:deep(.form-label) { color: #fff !important; }
:deep(.form-control) { color: #fff !important; background-color: transparent !important; }
:deep(.form-control::placeholder) { color: rgba(255, 255, 255, 0.7) !important; }
:deep(.form-control:focus) { color: #fff !important; }

.terms-preview {
  max-height: 220px;
  overflow-y: auto;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 6px;
  padding: 0.5rem 0.75rem;
  color: #fff;
}
.terms-pre {
  white-space: pre-wrap;
  word-wrap: break-word;
  font-family: inherit;
  font-size: 0.85rem;
  line-height: 1.4;
  margin: 0;
  color: #fff;
}
.form-check-input {
  margin-top: 0.3rem;
}
</style>
